import { Link } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { ChevronRight, ScrollText } from "lucide-react";
import ParticleField from "@/components/ParticleField";
import { ResourcesIllustration } from "@/components/illustrations/FintechIllustrations";

export default function Terms() {
  return (
    <div data-testid="terms-page">
      <Seo {...getSeoForPath("/terms")} />
      {/* HERO — matches the inner-page style used across the site */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />

        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)",
          }}
        />

        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">Terms of Service</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            {/* LEFT — text */}
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                <ScrollText className="w-3 h-3 text-[var(--swipe-accent)]" />
                Legal &amp; Compliance
              </div>
              <h1 style={{ color: "#FFFFFF" }} className="font-display-tight text-[2.5rem] sm:text-[3.25rem] lg:text-[3.75rem] leading-[1.05] font-extrabold tracking-[-0.02em]">
                Terms of <span className="text-[var(--swipe-accent)]">Service</span>
              </h1>
              <p className="mt-6 text-[17px] text-white/75 leading-[1.7] max-w-xl">
                The terms that govern your use of the Swipetronics website.
              </p>
              <p className="mt-5 text-[13px] text-white/55">
                Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
              </p>
            </div>

            {/* RIGHT — illustration */}
            <div className="relative">
              <ResourcesIllustration
                transparent
                className="relative w-full h-auto max-w-[520px] mx-auto drop-shadow-[0_30px_60px_rgba(0,0,0,0.35)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container-swipe py-16">
        <article className="mx-auto max-w-3xl text-[15px] leading-[1.8] text-slate-700">
          <LegalH2>1. Acceptance of terms</LegalH2>
          <p>By accessing or using <a href="https://swipetronics.com" className="text-[var(--swipe-accent)] underline">swipetronics.com</a> (the &quot;Site&quot;), you agree to these Terms of Service. If you do not agree, do not use the Site.</p>

          <LegalH2>2. What we do</LegalH2>
          <p>Swipetronics provides marketing information about merchant processing, point-of-sale equipment, and related programs, and allows businesses to request reviews, quotes, and applications. The Site is informational; final program approval, equipment availability, and pricing are subject to underwriting, business type, processing volume, applicable rules, and a separate merchant agreement.</p>
          <p className="mt-3"><strong>Submitting a request through the Site does not guarantee approval, pricing, equipment, savings, or program eligibility.</strong> Estimates of savings are illustrative and not guarantees.</p>

          <LegalH2>3. Submissions &amp; accuracy</LegalH2>
          <p>You agree that the information you submit through the Site (including savings-analysis, contact, quote, get-started, and funnel forms) is true, accurate, and that you are authorized to submit it on behalf of the business you represent.</p>
          <p className="mt-3"><strong>Contact permission.</strong> By submitting a form, you authorize Swipetronics to contact you by phone, text message, or email regarding your request. Message and data rates may apply.</p>

          <LegalH2>4. Eligibility</LegalH2>
          <p>The Site is intended for business users who are at least 18 years old. By using the Site, you confirm that you meet this requirement.</p>

          <LegalH2>5. Acceptable use</LegalH2>
          <p>You agree not to (a) misuse or interfere with the Site or its security, (b) attempt to access data not intended for you, (c) submit false, misleading, or unlawful content, or (d) use the Site to violate any law.</p>

          <LegalH2>6. Intellectual property</LegalH2>
          <p>All Site content — branding, text, illustrations, layouts, and software — is owned by Swipetronics or its licensors and is protected by intellectual-property laws. You may not copy, modify, or redistribute it without our prior written consent.</p>

          <LegalH2>7. Third-party services</LegalH2>
          <p>The Site may link to, or load assets from, third-party services (for example, email delivery, analytics, and advertising platforms). We are not responsible for third-party content or practices. Your use of those services is governed by their own terms.</p>

          <LegalH2>8. Disclaimer of warranties</LegalH2>
          <p>The Site is provided on an &quot;AS IS&quot; and &quot;AS AVAILABLE&quot; basis. To the maximum extent permitted by law, Swipetronics disclaims all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.</p>

          <LegalH2>9. Limitation of liability</LegalH2>
          <p>To the maximum extent permitted by law, Swipetronics will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or goodwill arising out of or related to your use of the Site.</p>

          <LegalH2>10. Indemnity</LegalH2>
          <p>You agree to indemnify and hold Swipetronics harmless from claims arising out of your misuse of the Site or violation of these Terms.</p>

          <LegalH2>11. Changes</LegalH2>
          <p>We may update these Terms from time to time. Continued use of the Site after changes are posted constitutes acceptance of the updated Terms.</p>

          <LegalH2>12. Governing law</LegalH2>
          <p>These Terms are governed by the laws of the State of New York, without regard to its conflict-of-laws principles.</p>

          <LegalH2>13. Contact</LegalH2>
          <p>Questions about these Terms? Email <a href="mailto:info@swipetronics.com" className="text-[var(--swipe-accent)] underline">info@swipetronics.com</a> or call <a href="tel:+18777947369" className="text-[var(--swipe-accent)] underline">(877) 794-7369</a>.</p>
        </article>
      </section>
    </div>
  );
}

function LegalH2({ children }) {
  return (
    <h2 className="font-display-tight text-2xl font-extrabold text-[var(--swipe-navy)] mt-10 mb-3">
      {children}
    </h2>
  );
}
